import React, { useState, useEffect } from 'react';
import { XMarkIcon, CalendarIcon, ClockIcon, CheckCircleIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { config } from '../config/environment';
import { endpoints } from '../config/endpoints';

interface EventType {
  id: string;
  name: string;
  duration_minutes: number;
  description: string | null;
}

interface Slot {
  start: string;
  end: string;
}

interface BookSessionModalProps {
  coachId: string;
  coachName: string;
  aiConsent?: boolean | null;
  onClose: () => void;
  onBooked: () => void;
}

function toDateInputMin(): string {
  return new Date().toISOString().split('T')[0];
}

function formatSlotTime(iso: string): string {
  // Input: UTC ISO string from backend; displayed in client's local timezone
  return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

const BookSessionModal: React.FC<BookSessionModalProps> = ({ coachId, coachName, aiConsent, onClose, onBooked }) => {
  const [eventTypes, setEventTypes] = useState<EventType[]>([]);
  const [etLoading, setEtLoading] = useState(true);
  const [etError, setEtError] = useState<string | null>(null);

  const [selectedEventType, setSelectedEventType] = useState<EventType | null>(null);
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [schedulingAvailable, setSchedulingAvailable] = useState(true);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const [booking, setBooking] = useState(false);
  const [bookError, setBookError] = useState<string | null>(null);
  const [booked, setBooked] = useState(false);

  // Package enrollment selection
  interface PackageOption { enrollment_id: string; package_name: string; sessions_used: number; sessions_total: number | null; exhausted: boolean; }
  const [packageOptions, setPackageOptions] = useState<PackageOption[]>([]);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string>('');

  const token = localStorage.getItem('authToken') || '';
  const authHeaders = { Authorization: `Bearer ${token}` };

  // Fetch event types on mount
  useEffect(() => {
    fetch(`${config.apiBaseUrl}${endpoints.main.scheduling.eventTypes(coachId)}`, { headers: authHeaders })
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then((data: EventType[]) => setEventTypes(data))
      .catch(() => setEtError('Could not load session types.'))
      .finally(() => setEtLoading(false));
  }, [coachId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch slots when date or event type changes
  useEffect(() => {
    if (!date || !selectedEventType) return;
    setSlotsLoading(true);
    setSlotsError(null);
    setSlots([]);
    setSelectedSlot(null);

    fetch(
      `${config.apiBaseUrl}${endpoints.main.scheduling.slots(coachId)}?date=${date}&event_type_id=${selectedEventType.id}`,
      { headers: authHeaders }
    )
      .then(r => r.ok ? r.json() : Promise.reject(r))
      .then(data => {
        setSchedulingAvailable(data.scheduling_available);
        setSlots(data.slots || []);
      })
      .catch(() => setSlotsError('Could not load available times. Please try again.'))
      .finally(() => setSlotsLoading(false));
  }, [date, selectedEventType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch active packages for this coach + event type whenever event type changes
  useEffect(() => {
    setPackageOptions([]);
    setSelectedEnrollmentId('');
    if (!selectedEventType?.id) return;
    fetch(
      `${config.apiBaseUrl}/packages/me/active-for-event-type?event_type_id=${selectedEventType.id}&coach_id=${coachId}`,
      { headers: authHeaders },
    )
      .then(r => r.ok ? r.json() : [])
      .then((data: PackageOption[]) => setPackageOptions(data))
      .catch(() => {});
  }, [selectedEventType?.id, coachId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleBook() {
    if (!selectedSlot || !selectedEventType) return;
    setBooking(true);
    setBookError(null);

    try {
      const resp = await fetch(
        `${config.apiBaseUrl}${endpoints.main.scheduling.book(coachId)}`,
        {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            start: selectedSlot.start,
            event_type_id: selectedEventType.id,
            ...(selectedEnrollmentId ? { package_enrollment_id: selectedEnrollmentId } : {}),
          }),
        }
      );
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error((err as { detail?: string }).detail || 'Booking failed');
      }
      setBooked(true);
      setTimeout(() => { onBooked(); onClose(); }, 1500);
    } catch (e: unknown) {
      setBookError(e instanceof Error ? e.message : 'Booking failed');
    } finally {
      setBooking(false);
    }
  }

  const step = !selectedEventType ? 'event-type' : 'datetime';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Book a Session</h2>
            <p className="text-sm text-gray-500 mt-0.5">with {coachName}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <XMarkIcon className="w-5 h-5 text-gray-400" />
          </button>
        </div>
        {aiConsent === false && (
          <div className="px-6 py-3 bg-red-50 border-b border-red-200">
            <p className="text-sm text-red-700">
              <span className="font-semibold">You have declined the AI usage policy.</span>{' '}
              Session notes will not be generated. Your coach is responsible for manually capturing notes.
            </p>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {booked ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <CheckCircleIcon className="w-12 h-12 text-green-500" />
              <p className="text-lg font-semibold text-gray-900">Session booked!</p>
              <p className="text-sm text-gray-500">You'll see it in your upcoming sessions.</p>
            </div>

          ) : step === 'event-type' ? (
            <>
              <p className="text-sm font-medium text-gray-700">Choose a session type</p>
              {etLoading && <p className="text-sm text-gray-400">Loading…</p>}
              {etError && <p className="text-sm text-red-500">{etError}</p>}
              {!etLoading && !etError && eventTypes.length === 0 && (
                <p className="text-sm text-gray-400">No session types available.</p>
              )}
              <div className="space-y-2">
                {eventTypes.map(et => (
                  <button
                    key={et.id}
                    onClick={() => setSelectedEventType(et)}
                    className="w-full flex items-center justify-between px-4 py-3 border border-gray-200 rounded-xl hover:border-indigo-300 hover:bg-indigo-50 transition-colors text-left"
                  >
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{et.name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {et.duration_minutes} min{et.description ? ` · ${et.description}` : ''}
                      </p>
                    </div>
                    <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            </>

          ) : (
            <>
              {/* Selected event type chip + back */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setSelectedEventType(null); setDate(''); setSlots([]); setSelectedSlot(null); }}
                  className="text-xs text-indigo-600 hover:underline"
                >
                  ← Back
                </button>
                <span className="px-2.5 py-1 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium">
                  {selectedEventType!.name} · {selectedEventType!.duration_minutes} min
                </span>
              </div>

              {/* Date picker */}
              <div>
                <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                  <CalendarIcon className="w-4 h-4" /> Pick a date
                </label>
                <input
                  type="date"
                  min={toDateInputMin()}
                  value={date}
                  onChange={e => setDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              {/* Time slots */}
              {date && (
                <div>
                  <p className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                    <ClockIcon className="w-4 h-4" /> Available times
                  </p>
                  {slotsLoading && <p className="text-sm text-gray-400">Loading…</p>}
                  {slotsError && <p className="text-sm text-red-500">{slotsError}</p>}
                  {!slotsLoading && !slotsError && !schedulingAvailable && (
                    <p className="text-sm text-gray-400">Online scheduling isn't available for this coach.</p>
                  )}
                  {!slotsLoading && !slotsError && schedulingAvailable && slots.length === 0 && (
                    <p className="text-sm text-gray-400">No available times on this date. Try another day.</p>
                  )}
                  {!slotsLoading && slots.length > 0 && (
                    <div className="grid grid-cols-3 gap-2">
                      {slots.map(slot => {
                        const isSelected = selectedSlot?.start === slot.start;
                        return (
                          <button
                            key={slot.start}
                            onClick={() => setSelectedSlot(slot)}
                            className={`px-3 py-2 rounded-lg text-sm font-medium border transition-colors ${
                              isSelected
                                ? 'bg-indigo-600 text-white border-indigo-600'
                                : 'bg-white text-gray-700 border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                            }`}
                          >
                            {formatSlotTime(slot.start)}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Package enrollment — optional, only shown if client has matching active packages */}
              {packageOptions.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Apply to package <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <select
                    value={selectedEnrollmentId}
                    onChange={e => setSelectedEnrollmentId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="">— No package —</option>
                    {packageOptions.map(p => (
                      <option key={p.enrollment_id} value={p.enrollment_id} disabled={p.exhausted}>
                        {p.package_name}
                        {p.sessions_total != null
                          ? ` (${p.sessions_used}/${p.sessions_total} used${p.exhausted ? ' — full' : ''})`
                          : ` (${p.sessions_used} used)`}
                      </option>
                    ))}
                  </select>
                  {selectedEnrollmentId && packageOptions.find(p => p.enrollment_id === selectedEnrollmentId)?.exhausted && (
                    <p className="mt-1 text-xs text-red-500">This package has no remaining sessions. Choose a different option.</p>
                  )}
                </div>
              )}

              {bookError && <p className="text-sm text-red-500">{bookError}</p>}
            </>
          )}
        </div>

        {/* Footer — book button on datetime step */}
        {!booked && step === 'datetime' && (() => {
          const exhaustedPicked = !!(selectedEnrollmentId && packageOptions.find(p => p.enrollment_id === selectedEnrollmentId)?.exhausted);
          return (
            <div className="px-6 pb-5 pt-3 border-t border-gray-100">
              <button
                onClick={handleBook}
                disabled={!selectedSlot || booking || exhaustedPicked}
                className="w-full py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-semibold rounded-xl transition-colors"
              >
                {booking ? 'Booking…' : selectedSlot
                  ? `Book ${formatSlotTime(selectedSlot.start)}`
                  : 'Select a time to book'}
              </button>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default BookSessionModal;
